// Moved all constructors for APIError subclasses here to avoid circular dependency issues
// between the subclasses and the superclass
import type {AxiosError} from 'axios'
import * as RA from 'ramda-adjunct'
import type {OmitStrict} from 'type-zoo'
import type {TFormError} from '@kapitalist/contract/types'
import APIFormError from '~/api/client/APIError/APIFormError'
import APIInvalidArgumentsError from '~/api/client/APIError/APIInvalidArgumentsError'
import APILoggedOutError from '~/api/client/APIError/APILoggedOutError'
import APIUnknownError from '~/api/client/APIError/APIUnknownError'
import type APIError from '~/api/client/APIError/base'
import type {
  IAPIErrorDataBase,
  IAPIErrorDataFormError,
  IAPIErrorDataUnknown,
} from '~/api/client/APIError/types'
import {isFormError, isInvalidArgumentsError} from '~/api/client/APIError/types'
import type {TGenericizedPathWithHTTPVerb} from '~/api/client/types'

type TAPIErrorDataCommonPieces = OmitStrict<IAPIErrorDataBase, 'kind'>

export const constructMockServerError = (statusCode: number) => {
  return new APIUnknownError({
    kind: 'unknown',
    statusCode,
    statusText: '',
    rawResponseData: undefined,
    contextualMessage: 'Mock error occurred during mock API call.',
    genericizedPathWithHTTPVerb: undefined,
  })
}

export const constructMockRequestFailure = (
  formError: TFormError,
  statusCode: number = 400
) => {
  return new APIFormError(
    fromFormError(formError, statusCode, 'Mock request failure', undefined)
  )
}

const unreachableServerErrorData = (): IAPIErrorDataUnknown => {
  return {
    kind: 'unknown',
    statusCode: 0,
    statusText: '',
    rawResponseData: undefined,
    contextualMessage:
      "We couldn't reach our servers. Please try refreshing the page.",
    genericizedPathWithHTTPVerb: undefined,
  }
}

export const constructUnreachableServerError = () =>
  new APIUnknownError(unreachableServerErrorData())

const fromFormError = (
  formError: TFormError,
  statusCode: number,
  statusText: string,
  genericizedPathWithHTTPVerb: TGenericizedPathWithHTTPVerb | undefined
): IAPIErrorDataFormError => {
  return {
    kind: 'formError',
    statusCode,
    statusText,
    formErrors: formError.errors,
    rawResponseData: formError,
    genericizedPathWithHTTPVerb,
  }
}

/**
 * Sorts out various errors we run into when hitting API endpoints
 * into one of the subclasses of APIError, depending on the error.
 */
export const buildFromAxiosError = (
  e: AxiosError,
  genericizedPathWithHTTPVerb: TGenericizedPathWithHTTPVerb // backend request
): APIError => {
  if (e.response) {
    // From the Axios docs: The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    const statusCode = e.response.status
    const statusText = e.response.statusText
    const rawResponseData = e.response.data

    let commonErrorData: TAPIErrorDataCommonPieces = {
      statusCode,
      statusText,
      rawResponseData,
      genericizedPathWithHTTPVerb,
    }

    if (
      statusCode === 401 &&
      ((isFormError(rawResponseData) && rawResponseData.errors.loggedOut) ||
        (RA.isPlainObj(rawResponseData) &&
          'message' in rawResponseData &&
          rawResponseData?.message === 'Not logged in')) // a Yesod default error we can get with cookie issues
    ) {
      return new APILoggedOutError({
        ...commonErrorData,
        kind: 'loggedOut',
      })
    } else if (isFormError(rawResponseData)) {
      // the error is in a shape we expect for form entry errors - a 'FormError' from the backend
      return new APIFormError({
        ...fromFormError(
          rawResponseData,
          statusCode,
          statusText,
          genericizedPathWithHTTPVerb
        ),
      })
    } else if (isInvalidArgumentsError(e.response.data)) {
      // the error is in another shape we expect for form entry errors
      return new APIInvalidArgumentsError({
        ...commonErrorData,
        kind: 'invalidArguments',
        invalidArgumentsError: e.response.data,
      })
    } else {
      // some other error response we don't understand / treat specially yet.
      // report 5xx errors to bugsnag
      if (statusCode > 499) {
        // reportFailedRequestToBugsnag(
        //   e,
        //   `Request to MWB failed with code ${statusCode}`,
        //   requestPath
        // )
      }

      console.error('Failed to load results from server.', {statusCode, statusText})

      return new APIUnknownError({
        ...commonErrorData,
        kind: 'unknown',
        contextualMessage:
          "We're having difficulty connecting to our server. Please try again.",
      })
    }
  } else if (e.request) {
    // From the Axios docs: The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser

    // this has the potential to annoy us if users have spotty internet connections...
    // reportFailedRequestToBugsnag(
    //   e,
    //   `Request to MWB received no response.`,
    //   requestPath
    // )

    return new APIUnknownError(unreachableServerErrorData())
  } else {
    // From the Axios docs: Something happened in setting up the request that triggered an Error

    // notifyBugsnag(
    //   {
    //     errorClass: 'mwb',
    //     errorMessage: 'Request to MWB could not be constructed.',
    //   },
    //   {
    //     metaData: {
    //       path: requestPath,
    //       error: {name: e.name, message: e.message, stack: e.stack},
    //     },
    //   }
    // )
    return new APIUnknownError(unreachableServerErrorData())
  }
}
